import * as React from 'react'
import { castingStoreAvailableCustomerPlanSubscriptions, castingStoreGetExpiringCustomerPlanSubscription, StoreProps } from '../../../../services/stores/casting.store'
import SelectableListComponent from '../../../shared/selectable-list.component'
import { DescriptionContainer, DescriptionParagraph, DescriptionTab } from '../casting-styled.component'
import { I18nService } from '../../../../services/utils/i18n.service'
import { CustomerPlanSubscriptionQuantityLeftInPeriodProgressBarComponent } from '../../../backoffice/customer-plan-subscriptions/customer-plan-subscription-quantity-left-in-period-progress-bar.component'
import { moment } from '../../../../services/utils/moment.service'
import CastingTooltipButtonComponent from '../casting-tooltip-button.component'
import RadioGroupComponent from '../../../shared/radio-group.component'
import { CustomerPlanSubscriptionBase } from '../../../../services/json-schemas/base/customer_plan_subscription'

const i18nScoped = new I18nService(require('./casting-customer-plan-subscriptions.component.yml'))

export default function CastingCustomerPlanSubscriptionsComponent ({ store, data: { offeredService, customer, customerPlanSubscriptionRemovePendingId, renderingMode } }: StoreProps) {
  const availableCustomerPlanSubscriptions = React.useMemo(() => castingStoreAvailableCustomerPlanSubscriptions({ customer, offeredService }).filter(_ => _.validated_at), [customer, offeredService])
  const [currentDescriptionTab, setCurrentDescriptionTab] = React.useState<'description' | 'quantity_left'>('quantity_left')
  const [selectedCustomerPlanSubscription, setSelectedCustomerPlanSubscription] = React.useState<typeof availableCustomerPlanSubscriptions[0]>(availableCustomerPlanSubscriptions[0] ?? null)

  const { expiringSubscription, delayInDays } = React.useMemo(
    () => castingStoreGetExpiringCustomerPlanSubscription({ customer, offeredService, customerPlanSubscriptionRemovePendingId }),
    [customer, offeredService, customerPlanSubscriptionRemovePendingId]
  )

  React.useEffect(() => {
    if (expiringSubscription?.expiration_action && !store.data.customerPlanSubscriptionExpirationAction) {
      store.set({ customerPlanSubscriptionExpirationAction: { id: expiringSubscription.id, expiration_action: expiringSubscription.expiration_action } })
    }
  }, [expiringSubscription, store])

  const usageJsx = React.useMemo(() => {
    if (!selectedCustomerPlanSubscription || currentDescriptionTab !== 'quantity_left') return null

    const quantityLeftSimulationResult = customer.customerPlanSubscriptionSimulationResult.quantityLeftForCustomerPlanSubscriptions.find(_ => _.customer_plan_subscription.id === selectedCustomerPlanSubscription.id)

    return Object.entries(quantityLeftSimulationResult.quantity_left_by_offset).map(([quantityFrequencyOffset, quantityLeft]) => {
      const momentFrequency = selectedCustomerPlanSubscription.customer_plan.quantity_frequency === 'weekly' ? 'week' : 'month'
      const startDate = moment(selectedCustomerPlanSubscription.validated_at).add(quantityFrequencyOffset, momentFrequency)

      return <div key={ startDate.toString() } style={{ padding: '0.5em 0.5em 0 0.5em' }}>
        <CustomerPlanSubscriptionQuantityLeftInPeriodProgressBarComponent customerPlan={ selectedCustomerPlanSubscription.customer_plan } quantityLeft={ quantityLeft } startDate={ startDate } />
      </div>
    })
  }, [currentDescriptionTab, customer.customerPlanSubscriptionSimulationResult, selectedCustomerPlanSubscription])

  const expirationChoices = React.useMemo(() => {
    if (!expiringSubscription) return []
    const endDateFormatted = moment(expiringSubscription.expires_at).format('L')
    const planIsAvailable = expiringSubscription.customer_plan.active && !expiringSubscription.customer_plan.deleted_at

    return [
      planIsAvailable && { value: 'renew_plan' as const, text: i18nScoped.t('action_renew_plan'), tooltip: i18nScoped.t('action_renew_plan_tooltip', { end_date: endDateFormatted }) },
      { value: 'keep_events' as const, text: i18nScoped.t('action_keep_events'), tooltip: i18nScoped.t('action_keep_events_tooltip', { end_date: endDateFormatted }) },
      { value: 'cancel_events' as const, text: i18nScoped.t('action_cancel_events'), tooltip: i18nScoped.t('action_cancel_events_tooltip', { end_date: endDateFormatted }) }
    ].filter(Boolean) as { value: CustomerPlanSubscriptionBase['expiration_action'], text: string, tooltip: string }[]
  }, [expiringSubscription])

  const handleExpirationActionChange = React.useCallback((action: CustomerPlanSubscriptionBase['expiration_action']) => {
    if (expiringSubscription) store.set({ customerPlanSubscriptionExpirationAction: { id: expiringSubscription.id, expiration_action: action } })
  }, [expiringSubscription, store])

  const nextButton = <CastingTooltipButtonComponent
    id="js-test-tooltip-next"
    renderingMode={ renderingMode }
    onClick={ () => store.nextFocus({ closeOnMobile: true }) }
    disabled={ store.getProgressForStep('customer-plan') !== 100 }
    children={ i18nScoped.t('next') } />

  // The customer plan subscription expires soon, the customer must express how to handle the expiration.
  // Depending on its preferences, he may not book dates after the expiration (ex. if it terminates the subscription with the events)
  if (expiringSubscription?.policy['update_expiration_action?'] && expiringSubscription.expiration_action !== 'keep_events') {
    return <>
      <div>{ i18nScoped.t('expiration_alert', { count: delayInDays }) }</div>
      <div>{ i18nScoped.t('expiration_question') }</div>

      <RadioGroupComponent
        name={ `customer-plan-expiration-${expiringSubscription.id}` }
        choices={ expirationChoices }
        value={ store.data.customerPlanSubscriptionExpirationAction?.expiration_action ?? null }
        onChange={ handleExpirationActionChange }
        className="u-mtm" />

      { nextButton }
    </>
  }

  return <>
    <p>{ availableCustomerPlanSubscriptions.length > 1 && i18nScoped.t('subtitle_1') } { i18nScoped.t('subtitle_2') }</p>

    <SelectableListComponent
      name="customerPlanSubscription"
      value={ selectedCustomerPlanSubscription }
      data={ availableCustomerPlanSubscriptions }
      formatter={ customerPlanSubscription => customerPlanSubscription.title }
      onChange={ val => setSelectedCustomerPlanSubscription(val) } />

    { selectedCustomerPlanSubscription && expiringSubscription?.expiration_action === 'keep_events' && <div className="u-mtm box">
      <div><strong>{ i18nScoped.t('expiration_already_decided', { count: delayInDays }) }</strong></div>
    </div> }

    { selectedCustomerPlanSubscription && !expiringSubscription?.policy['update_expiration_action?'] && <DescriptionContainer className="u-mtm">
      <div style={{ display: 'flex' }}>
        { (['description', 'quantity_left'] as const).map(tab => <DescriptionTab key={ tab } onClick={ () => setCurrentDescriptionTab(tab) } $active={ currentDescriptionTab === tab }>
          { i18nScoped.t(`description_tabs.${tab}`) }
        </DescriptionTab>) }
      </div>

      { currentDescriptionTab === 'description' && <DescriptionParagraph>
        { (selectedCustomerPlanSubscription.customer_plan.description ?? '') !== '' && <div style={{ marginBottom: '0.5em' }} dangerouslySetInnerHTML={{ __html: selectedCustomerPlanSubscription.customer_plan.description }} /> }

        <section>
          <strong style={{ fontSize: '1.1em' }}>{ i18nScoped.t('customer_plan_subscription_offered_services_label') }</strong>
          <ul>{ selectedCustomerPlanSubscription.offered_services.map(offeredService => <li key={ offeredService.id }>{ offeredService.name }</li>) }</ul>
        </section>
      </DescriptionParagraph> }

      { currentDescriptionTab === 'quantity_left' && <div style={{ maxHeight: '15em', overflow: 'auto' }} children={ usageJsx } /> }
    </DescriptionContainer> }

    { nextButton }
  </>
}
